import { publish } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import { uniq } from '@grubhubprod/umami-library/dist/nodash/uniq';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { SnackbarStatus } from 'src/components/snackbar/SnackbarLauncher';
import {
  API_ERROR_MESSAGES_KEYS,
  DEFAULT_ERROR_MESSAGE,
} from './apiErrorMessages';
import { IError, IErrorResponse, SdkErrorResponse } from './types';

export function pushErrorFromAPI(
  error: IErrorResponse | SdkErrorResponse,
  {
    fallbackMessage,
    errorMessageCallback,
  }: {
    fallbackMessage?: string;
    errorMessageCallback?: (errorMessages: string[]) => void;
  } = {}
): void {
  const errorsArray = extractErrorsFromResponse(error);
  const errorMessages = processErrorMessages(
    errorsArray,
    error.status,
    fallbackMessage
  );

  if (errorMessageCallback) {
    const uniqueAndSortedErrorMessages = uniq(errorMessages).sort();
    errorMessageCallback(uniqueAndSortedErrorMessages);
  }
}

function extractErrorsFromResponse(
  error: IErrorResponse | SdkErrorResponse
): IError[] {
  // handle the SdkErrorResponse (from sdk.http calls)
  if ('data' in error) {
    const errorData = get(error, 'data');
    return Array.isArray(errorData) ? errorData : [];
  }

  // handle the IErrorResponse
  const errorData = get(error, 'errors');
  return Array.isArray(errorData) ? errorData : [];
}

function processErrorMessages(
  errorsArray: IError[],
  status: number,
  fallbackMessage?: string
): string[] {
  const errorMessages: string[] = [];

  if (status === 422 && errorsArray.length > 0) {
    errorsArray.forEach((error) => {
      const messageKey = `${error.property}.${error.message_key}`.toLowerCase();
      const errorMessage = getErrorMessage(messageKey, error);
      errorMessages.push(errorMessage.toLowerCase());
      pushError(errorMessage);
    });
  } else {
    const errorMessage = fallbackMessage || DEFAULT_ERROR_MESSAGE;
    errorMessages.push(errorMessage.toLowerCase());
    pushError(errorMessage);
  }

  return errorMessages;
}

function getErrorMessage(messageKey: string, error: IError): string {
  // Check if we have a user-friendly message for this error
  const userFriendlyMessage = API_ERROR_MESSAGES_KEYS[messageKey];

  if (userFriendlyMessage) {
    return userFriendlyMessage;
  }

  // Fallback to showing the property and message key for unmapped errors
  return `${error.property} ${error.message_key}`;
}

export function pushSuccess(message: string): void {
  publish('push:snackbar', {
    status: SnackbarStatus.SUCCESS,
    message,
  });
}

export function pushError(message: string): void {
  publish('push:snackbar', {
    status: SnackbarStatus.ERROR,
    message,
  });
}
